/**
 * useEnrollmentsFiltering
 *
 * Filter and sort enrollments by provider, payer, status, billing-ready, risk/age.
 * Assumption: large provider × payer matrices; pagination handled by parent.
 */

import { useMemo } from 'react';
import type { Enrollment, EnrollmentsFilters } from '../types';

export function useEnrollmentsFiltering(
  enrollments: Enrollment[],
  filters: EnrollmentsFilters
): { filteredEnrollments: Enrollment[]; hasActiveFilters: boolean } {
  const hasActiveFilters =
    (filters.providerSearch?.trim() ?? '') !== '' ||
    (filters.payerSearch?.trim() ?? '') !== '' ||
    filters.status !== 'all' ||
    filters.billingReadyOnly ||
    filters.sortBy !== 'default';

  const filteredEnrollments = useMemo(() => {
    let list = [...enrollments];

    const providerLower = (filters.providerSearch?.trim() ?? '').toLowerCase();
    if (providerLower) {
      list = list.filter((e) => e.providerName.toLowerCase().includes(providerLower));
    }
    const payerLower = (filters.payerSearch?.trim() ?? '').toLowerCase();
    if (payerLower) {
      list = list.filter((e) => e.payerName.toLowerCase().includes(payerLower));
    }
    if (filters.status !== 'all') {
      list = list.filter((e) => e.status === filters.status);
    }
    if (filters.billingReadyOnly) {
      list = list.filter((e) => e.billingReady);
    }

    if (filters.sortBy === 'risk') {
      list.sort((a, b) => {
        const aBlockers = (a.blockers?.length ?? 0) + (a.status === 'denied' ? 2 : 0);
        const bBlockers = (b.blockers?.length ?? 0) + (b.status === 'denied' ? 2 : 0);
        if (bBlockers !== aBlockers) return bBlockers - aBlockers;
        const aStalled = a.status === 'pending-payer-response' || a.status === 'in-progress' ? (a.daysInStage ?? 0) : 0;
        const bStalled = b.status === 'pending-payer-response' || b.status === 'in-progress' ? (b.daysInStage ?? 0) : 0;
        return bStalled - aStalled;
      });
    } else if (filters.sortBy === 'age') {
      list.sort((a, b) => {
        const aDays = a.daysInStage ?? 0;
        const bDays = b.daysInStage ?? 0;
        return bDays - aDays;
      });
    }

    return list;
  }, [enrollments, filters]);

  return { filteredEnrollments, hasActiveFilters };
}
